import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getGetAssessmentsOptionsQueryKey,
  useCreateAccumulationTag,
  useDeleteAccumulationTag,
  useGetAssessmentsOptions,
  useUpdateAccumulationTag,
} from '@/entities/assessments/api/generated/api';
import { GetAssessmentsOptionsFieldsItem } from '@/entities/assessments/api/generated/model';
import { invalidateByPathPrefix } from '@/shared/lib/query/invalidateByPathPrefix';
import { type TagNameError, validateTagName } from '@/shared/lib/validation/validateTagName';
import { toast } from '@/shared/ui';
import {
  type TagOption,
  TagSelectWithCrud,
} from '@/shared/ui/tag-select-with-crud/TagSelectWithCrud';

const OPTIONS_PARAMS = { fields: [GetAssessmentsOptionsFieldsItem.accumulation_tag] };

type Props = {
  selectedTagIds: string[];
  onSelectedTagIdsChange: (tagIds: string[]) => void;
  isAdmin?: boolean;
};

export const AccumulationTagSelectWithCrud = ({
  selectedTagIds,
  onSelectedTagIdsChange,
  isAdmin = false,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // 非同期コールバックで最新の選択 ID にアクセスするための ref
  const latestSelectedRef = useRef(selectedTagIds);
  latestSelectedRef.current = selectedTagIds;

  const { data: optionsData } = useGetAssessmentsOptions(OPTIONS_PARAMS);

  const tags: TagOption[] = (optionsData?.accumulation_tags ?? []).map((tag) => ({
    id: tag.id,
    name: tag.name,
  }));

  const validationMessage = (code: TagNameError) => {
    const map: Record<TagNameError, string> = {
      required: t('tag.validation.required'),
      maxLength: t('tag.validation.maxLength'),
      duplicate: t('tag.validation.duplicate'),
    };
    return map[code];
  };

  const invalidateOptions = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssessmentsOptionsQueryKey(OPTIONS_PARAMS) });
  };

  // タグ名変更・削除時はテーブル/サイドパネル側に埋め込まれたタグ情報も再取得が必要
  const invalidateAccumulationData = () => {
    invalidateByPathPrefix(queryClient, {
      include: ['/assessments/comparisons', '/assessments/accumulations'],
    });
  };

  const { mutate: createTag } = useCreateAccumulationTag({ request: { suppressToast: true } });
  const { mutateAsync: updateTagAsync } = useUpdateAccumulationTag({
    request: { suppressToast: true },
  });
  const { mutate: deleteTag } = useDeleteAccumulationTag({ request: { suppressToast: true } });

  const handleCreateTag = (name: string) => {
    const error = validateTagName(tags, name);
    if (error) {
      toast.error(validationMessage(error));
      return;
    }
    createTag(
      { data: { name: name.trim() } },
      {
        onSuccess: (response) => {
          toast.success(t('tag.messages.createSuccess'));
          onSelectedTagIdsChange([...latestSelectedRef.current, response.id]);
          invalidateOptions();
        },
        onError: () => {
          toast.error(t('common.messages.create-failed', { name: t('assessment.memo.tag') }));
        },
      },
    );
  };

  const handleUpdateTag = async (tagId: string, name: string): Promise<boolean> => {
    const error = validateTagName(tags, name, tagId);
    if (error) {
      toast.error(validationMessage(error));
      return false;
    }
    try {
      await updateTagAsync({ accumulationTagId: tagId, data: { name: name.trim() } });
      toast.success(t('tag.messages.updateSuccess'));
      invalidateOptions();
      invalidateAccumulationData();
      return true;
    } catch {
      toast.error(t('common.messages.update-failed', { name: t('assessment.memo.tag') }));
      return false;
    }
  };

  const handleDeleteTag = (tagId: string) => {
    deleteTag(
      { accumulationTagId: tagId },
      {
        onSuccess: () => {
          toast.success(t('tag.messages.deleteSuccess'));
          // 紐付けは BE 側でカスケード削除されるため、紐付け API は呼ばない。
          // invalidateAccumulationData() の refetch で useAccumulationTagAssignment が
          // initialTagIds 経由で selectedTagIds を同期する。
          invalidateOptions();
          invalidateAccumulationData();
        },
        onError: () => {
          toast.error(t('common.messages.delete-failed', { name: t('assessment.memo.tag') }));
        },
      },
    );
  };

  return (
    <TagSelectWithCrud
      tags={tags}
      selectedTagIds={selectedTagIds}
      onSelectedTagIdsChange={onSelectedTagIdsChange}
      onCreateTag={handleCreateTag}
      onUpdateTag={handleUpdateTag}
      onDeleteTag={handleDeleteTag}
      isAdmin={isAdmin}
    />
  );
};
